import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Send } from 'lucide-react';
import { useProcessarInutilizacaoMutation } from '@/services/migrate';
import { InutilizacaoResponse } from '@/@types/migrate';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StoreSelect from './store-select';

const InutilizacaoTab = () => {
  const [cnpj, setCnpj] = useState('');
  const [serie, setSerie] = useState('');
  const [numero, setNumero] = useState(0);
  const [justificativa, setJustificativa] = useState('Erro sistêmico - Regularização de numeração fiscal');
  const [resultado, setResultado] = useState<InutilizacaoResponse | null>(null);

  const [processarInutilizacao, { isLoading }] = useProcessarInutilizacaoMutation();

  const handleSubmit = useCallback(async () => {
    // Validação básica
    if (!cnpj || !serie || numero <= 0) {
      alert('Preencha todos os campos corretamente');
      return;
    }

    try {
      const response = await processarInutilizacao({
        documentos: [
          {
            cnpj,
            serie,
            numeroInicial: numero,
            numeroFinal: numero,
            justificativa,
          },
        ],
        delaySegundos: 10, // Valor fixo
      }).unwrap();

      setResultado(response);

      // Reset form
      setCnpj('');
      setSerie('');
      setNumero(0);
      setJustificativa('Erro sistêmico - Regularização de numeração fiscal');
    } catch (error: any) {
      console.error('Erro ao processar inutilização:', error);
      alert(`Erro ao processar: ${error?.data?.message || error.message}`);
    }
  }, [cnpj, serie, numero, justificativa, processarInutilizacao]);

  return (
    <div className="space-y-6">
      {/* Formulário de Inutilização */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Nova Inutilização</h2>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="cnpj">Loja / CNPJ</Label>
              <StoreSelect
                value={cnpj}
                onChange={setCnpj}
                disabled={isLoading}
              />
            </div>

            <div>
              <Label htmlFor="serie">Série</Label>
              <Input
                id="serie"
                value={serie}
                onChange={(e) => setSerie(e.target.value)}
                placeholder="1"
                disabled={isLoading}
              />
            </div>

            <div>
              <Label htmlFor="numero">Número do Documento</Label>
              <Input
                id="numero"
                type="number"
                value={numero}
                onChange={(e) => setNumero(Number(e.target.value))}
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="justificativa">Justificativa</Label>
            <Textarea
              id="justificativa"
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              rows={2}
              disabled={isLoading}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSubmit} disabled={isLoading} className="flex items-center gap-2">
              {isLoading ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Processando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Processar Inutilização
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Métricas */}
      {resultado && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="p-6">
            <div className="text-sm text-gray-600">Total Processados</div>
            <div className="text-3xl font-bold mt-2">{resultado.totalProcessados}</div>
          </Card>
          <Card className="p-6">
            <div className="text-sm text-gray-600">Sucessos</div>
            <div className="text-3xl font-bold mt-2 text-green-600">{resultado.totalSucesso}</div>
          </Card>
          <Card className="p-6">
            <div className="text-sm text-gray-600">Erros</div>
            <div className="text-3xl font-bold mt-2 text-red-600">{resultado.totalErros}</div>
          </Card>
        </div>
      )}

      {/* Tabela de Resultados */}
      {resultado && resultado.resultados && resultado.resultados.length > 0 && (
        <Card>
          <div className="p-4 border-b">
            <h3 className="font-semibold">Resultados da Última Operação</h3>
            <p className="text-sm text-gray-600">
              Tempo total: {(Number(resultado.tempoTotalProcessamento) / 1000).toFixed(2)}s
            </p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>CNPJ</TableHead>
                <TableHead>Série</TableHead>
                <TableHead>Número</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Mensagem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {resultado.resultados.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.cnpj}</TableCell>
                  <TableCell>{item.serie}</TableCell>
                  <TableCell>{item.numeroInicial}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        item.sucesso
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {item.sucesso ? 'Sucesso' : 'Erro'}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-md truncate">
                    {item.sucesso ? "" : item.erro}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {!resultado && (
        <Card className="p-12 text-center text-gray-500">
          <p>Nenhuma inutilização processada ainda.</p>
          <p className="text-sm mt-2">Preencha o formulário acima para processar uma inutilização.</p>
        </Card>
      )}
    </div>
  );
};

export default InutilizacaoTab;
